import { Extension, Mark, Node } from '@tiptap/react';
import { NodeSelection, Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import DOMPurify from 'dompurify';

/**
 * What an article body can hold beyond text, as editor nodes, so opening and
 * saving a body never drops it (StarterKit alone turns every <img>, <figure>
 * and <iframe> into nothing):
 *
 *   bodyImage  <img> or <figure><img><figcaption>…</figcaption></figure>
 *   bodyVideo  the site's video player (figure.site-video) or a placeholder
 *              <img src="video:<assetId>" alt="…" title="caption"> that the
 *              server turns into the player when it saves (bodyVideos.js)
 *   htmlBlock  anything else block-level (tables, styled boxes, other media),
 *              kept byte for byte and shown read-only
 *
 * The public site's sanitizer (smagent-sites src/lib/sanitize.ts) decides what
 * readers see; these only keep what is there.
 */

type Attrs = Record<string, string | null>;

const attr = (el: Element, name: string) => el.getAttribute(name) || null;

/** Attributes worth carrying on an <img>. */
const IMG_ATTRS = ['src', 'alt', 'title', 'srcset', 'sizes', 'width', 'height'] as const;

const imgAttrs = (img: Element): Attrs =>
  Object.fromEntries(IMG_ATTRS.map((k) => [k, attr(img, k)]));

const present = (attrs: Attrs) =>
  Object.fromEntries(Object.entries(attrs).filter(([, v]) => v !== null && v !== ''));

const nullAttrs = (names: readonly string[]) =>
  Object.fromEntries(
    names.map((n) => [n, { default: null, parseHTML: () => null, rendered: false }])
  );

const isVideoSrc = (src: string | null) => /^video:/i.test(src || '');
const STREAM = /^https:\/\/customer-[a-z0-9]+\.cloudflarestream\.com\/[a-f0-9]{32}\/iframe/i;

/** A caption label and a remove hint, shared by the media node views. */
function mediaShell(kind: string) {
  const dom = document.createElement('div');
  dom.className = 'ink-be-media';
  dom.dataset.kind = kind;
  dom.contentEditable = 'false';
  return dom;
}

export const BodyImage = Node.create({
  name: 'bodyImage',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return nullAttrs([...IMG_ATTRS, 'caption']);
  },

  parseHTML() {
    return [
      {
        // A plain figure: one image and maybe a caption. Anything richer (a
        // class, a link, <picture>) is kept whole by htmlBlock instead.
        tag: 'figure',
        priority: 60,
        getAttrs: (node) => {
          const el = node as HTMLElement;
          if (el.attributes.length) return false;
          const kids = Array.from(el.children);
          const imgs = kids.filter((c) => c.tagName === 'IMG');
          if (imgs.length !== 1 || kids.some((c) => !['IMG', 'FIGCAPTION'].includes(c.tagName))) {
            return false;
          }
          if (isVideoSrc(attr(imgs[0], 'src'))) return false;
          return {
            ...imgAttrs(imgs[0]),
            caption: el.querySelector('figcaption')?.textContent?.trim() || null,
          };
        },
      },
      {
        tag: 'img[src]',
        getAttrs: (node) => {
          const el = node as HTMLElement;
          if (isVideoSrc(attr(el, 'src'))) return false;
          // Inside a link or <picture> it can't stand alone without losing them.
          if (el.closest('a, picture')) return false;
          return imgAttrs(el);
        },
      },
    ];
  },

  renderHTML({ node }) {
    const { caption, ...rest } = node.attrs as Attrs;
    const img: [string, Attrs] = ['img', present(rest)];
    return caption ? ['figure', {}, img, ['figcaption', {}, caption]] : img;
  },

  addNodeView() {
    return ({ node: initial }) => {
      const dom = mediaShell('image');
      const render = (attrs: Attrs) => {
        dom.replaceChildren();
        const img = document.createElement('img');
        img.src = attrs.src || '';
        img.alt = attrs.alt || '';
        dom.appendChild(img);
        const meta = document.createElement('div');
        meta.className = 'ink-be-media-meta';
        const alt = document.createElement('span');
        alt.dataset.ok = attrs.alt ? 'true' : 'false';
        alt.textContent = attrs.alt ? `Alt: ${attrs.alt}` : 'No alt text';
        meta.appendChild(alt);
        if (attrs.caption) {
          const cap = document.createElement('span');
          cap.textContent = `Caption: ${attrs.caption}`;
          meta.appendChild(cap);
        }
        dom.appendChild(meta);
      };
      render(initial.attrs as Attrs);
      return {
        dom,
        update: (next) => {
          if (next.type.name !== 'bodyImage') return false;
          render(next.attrs as Attrs);
          return true;
        },
      };
    };
  },
});

const VIDEO_ATTRS = ['src', 'title', 'caption', 'aspect', 'posterImage', 'posterTime'] as const;

export const BodyVideo = Node.create({
  name: 'bodyVideo',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return nullAttrs(VIDEO_ATTRS);
  },

  parseHTML() {
    return [
      {
        tag: 'figure.site-video',
        priority: 70,
        getAttrs: (node) => {
          const el = node as HTMLElement;
          const frame = el.querySelector('iframe');
          if (!frame) return false;
          return {
            src: attr(frame, 'src'),
            title: attr(frame, 'title'),
            caption: el.querySelector('figcaption')?.textContent?.trim() || null,
            aspect: attr(el, 'data-aspect'),
            posterImage: attr(el, 'data-poster-image'),
            posterTime: attr(el, 'data-poster-time'),
          };
        },
      },
      {
        tag: 'img[src]',
        priority: 60,
        getAttrs: (node) => {
          const el = node as HTMLElement;
          if (!isVideoSrc(attr(el, 'src'))) return false;
          return { src: attr(el, 'src'), title: attr(el, 'alt'), caption: attr(el, 'title') };
        },
      },
      {
        tag: 'iframe[src]',
        priority: 60,
        getAttrs: (node) => {
          const el = node as HTMLElement;
          if (!STREAM.test(attr(el, 'src') || '')) return false;
          return { src: attr(el, 'src'), title: attr(el, 'title') };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const a = node.attrs as Attrs;
    // Not saved yet: the placeholder the server turns into the player.
    if (isVideoSrc(a.src)) {
      return ['img', present({ src: a.src, alt: a.title, title: a.caption })];
    }
    // The stored player, as the server writes it (videoFigure in bodyVideos.js).
    const figure = present({
      class: 'site-video',
      'data-aspect': a.aspect,
      'data-poster-image': a.posterImage,
      'data-poster-time': a.posterImage ? null : a.posterTime,
    });
    const frame = [
      'iframe',
      present({
        src: a.src,
        title: a.title,
        loading: 'lazy',
        allow:
          'accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen',
        allowfullscreen: 'true',
      }),
    ];
    return a.caption
      ? ['figure', figure, frame, ['figcaption', {}, a.caption]]
      : ['figure', figure, frame];
  },

  addNodeView() {
    return ({ node: initial }) => {
      const dom = mediaShell('video');
      const render = (a: Attrs) => {
        dom.replaceChildren();
        const frame = document.createElement('div');
        frame.className = 'ink-be-video';
        if (a.src && STREAM.test(a.src)) {
          const iframe = document.createElement('iframe');
          iframe.src = a.src;
          iframe.title = a.title || 'Video';
          iframe.setAttribute('loading', 'lazy');
          iframe.tabIndex = -1;
          frame.appendChild(iframe);
        } else {
          frame.textContent = 'Video · the player appears once this saves';
        }
        dom.appendChild(frame);
        const meta = document.createElement('div');
        meta.className = 'ink-be-media-meta';
        const what = document.createElement('span');
        what.dataset.ok = a.title ? 'true' : 'false';
        what.textContent = a.title ? `Video: ${a.title}` : 'Video with no description';
        meta.appendChild(what);
        if (a.caption) {
          const cap = document.createElement('span');
          cap.textContent = `Caption: ${a.caption}`;
          meta.appendChild(cap);
        }
        dom.appendChild(meta);
      };
      render(initial.attrs as Attrs);
      return {
        dom,
        update: (next) => {
          if (next.type.name !== 'bodyVideo') return false;
          render(next.attrs as Attrs);
          return true;
        },
      };
    };
  },
});

/** Kept whole: these never become editor text. */
const KEPT_TAGS = [
  'table',
  'figure',
  'picture',
  'video',
  'audio',
  'iframe',
  'object',
  'embed',
  'svg',
  'dl',
  'details',
  'form',
];
/** Wrappers kept only when they carry something (a class, a style, an id). */
const KEPT_WRAPPERS = ['div', 'section', 'aside', 'article', 'header', 'footer', 'nav'];

export const HtmlBlock = Node.create({
  name: 'htmlBlock',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return { html: { default: '', parseHTML: () => null, rendered: false } };
  },

  parseHTML() {
    return [
      ...KEPT_TAGS.map((tag) => ({
        tag,
        priority: 40,
        getAttrs: (node: HTMLElement | string) => ({ html: (node as HTMLElement).outerHTML }),
      })),
      {
        // A linked image (WordPress posts have many): an image can't carry a
        // link mark, so the link and its image are kept together.
        tag: 'a',
        priority: 1001,
        getAttrs: (node: HTMLElement | string) => {
          const el = node as HTMLElement;
          const media = el.querySelector('img, picture');
          return media && !(el.textContent || '').trim() ? { html: el.outerHTML } : false;
        },
      },
      ...KEPT_WRAPPERS.map((tag) => ({
        tag,
        priority: 40,
        getAttrs: (node: HTMLElement | string) => {
          const el = node as HTMLElement;
          return el.attributes.length ? { html: el.outerHTML } : false;
        },
      })),
    ];
  },

  renderHTML({ node }) {
    const t = document.createElement('template');
    t.innerHTML = String(node.attrs.html || '');
    return (t.content.firstElementChild as HTMLElement | null) ?? ['div', {}];
  },

  addNodeView() {
    return ({ node }) => {
      const dom = mediaShell('kept');
      const preview = document.createElement('div');
      preview.className = 'ink-be-kept';
      preview.innerHTML = DOMPurify.sanitize(String(node.attrs.html || ''), {
        FORBID_TAGS: ['iframe', 'object', 'embed', 'form'],
      });
      const note = document.createElement('div');
      note.className = 'ink-be-media-meta';
      note.textContent = 'Kept as is. To change this part, ask your agent.';
      dom.append(preview, note);
      return { dom };
    };
  },
});

const simpleMark = (name: string, tag: string) =>
  Mark.create({
    name,
    parseHTML: () => [{ tag }],
    renderHTML: () => [tag, 0],
  });

/** Inline marks StarterKit doesn't have, so they survive a save. */
export const Superscript = simpleMark('superscript', 'sup');
export const Subscript = simpleMark('subscript', 'sub');
export const Highlight = simpleMark('highlight', 'mark');

/** Outlines the top-level block the cursor is in (the design's paragraph boxes). */
export const ActiveBlock = Extension.create({
  name: 'activeBlock',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('activeBlock'),
        props: {
          decorations(state) {
            const sel = state.selection;
            let from: number;
            let size: number;
            if (sel instanceof NodeSelection && sel.$from.depth === 0) {
              from = sel.from;
              size = sel.node.nodeSize;
            } else if (sel.$from.depth >= 1) {
              from = sel.$from.before(1);
              size = sel.$from.node(1).nodeSize;
            } else {
              return null;
            }
            return DecorationSet.create(state.doc, [
              Decoration.node(from, from + size, { class: 'is-active' }),
            ]);
          },
        },
      }),
    ];
  },
});

/**
 * An image or video on its own line arrives wrapped in <p> (markdown does
 * that); a block can't sit in a paragraph, so the editor would split it and
 * leave an empty box behind. Lift those out first.
 */
export function liftLoneMedia(html: string): string {
  if (!html || !/<p[\s>]/i.test(html) || !/<(img|iframe)/i.test(html)) return html;
  const t = document.createElement('template');
  t.innerHTML = html;
  let changed = false;
  t.content.querySelectorAll('p').forEach((p) => {
    const kids = Array.from(p.childNodes).filter(
      (n) => !(n.nodeType === 3 && !(n.textContent || '').trim())
    );
    const only = kids.length === 1 && kids[0].nodeType === 1 ? (kids[0] as Element) : null;
    const lone =
      only &&
      (['IMG', 'IFRAME'].includes(only.tagName) ||
        (only.tagName === 'A' && only.querySelector('img') && !(only.textContent || '').trim()));
    if (lone) {
      p.replaceWith(only);
      changed = true;
    }
  });
  return changed ? t.innerHTML : html;
}

/**
 * Text and media counts, compared before and after the editor reads a body:
 * any difference means saving would lose something, so the editor stays shut.
 */
export function bodySignature(html: string) {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  doc.querySelectorAll('script, style, template').forEach((n) => n.remove());
  const count = (sel: string) => doc.querySelectorAll(sel).length;
  return [
    (doc.body.textContent || '').replace(/\s+/g, ''),
    count('img'),
    count('iframe'),
    count('video, audio'),
    count('table'),
    count('a[href]'),
  ].join('\u0000');
}
